<template>
  <Dialog
    :open="open"
    @update:open="$emit('update:open', $event)"
    :size="maxSmall ? 'full' : undefined"
    :side="maxSmall ? 'default' : 'right'"
    :showFooter="false"
  >
    <template #header>
      <NotificationsHeader @openSettings="openSettings = true" />
    </template>
    <template #content>
      <div class="flex flex-col grow bg-primary overflow-y-hidden">
        <Tabs
          v-model="activeTabIndex"
          variant="notifications"
          :tabVariant="maxSmall ? 'underline' : 'button-primary'"
          :size="maxSmall ? 'xl' : 'sm'"
          :fullWidth="maxSmall"
        >
          <template v-if="!maxSmall" #end>
            <NotificationActions
              @markAllAsRead="markAllAsRead"
              @openSettings="openSettings = true"
            />
          </template>
          <Tab v-for="tab in tabs" :key="tab.id" v-bind="tab">
            <div class="w-full flex flex-col grow overflow-y-auto">
              <div v-if="!notifications.length" class="flex flex-col grow px-4">
                <State
                  type="NoNotifications"
                  :title="$t('components.empty_states.notifications.title')"
                  :text="
                    $t('components.empty_states.notifications.description')
                  "
                  class="grow"
                />
              </div>
              <div
                v-else
                :class="
                  cn('w-full flex flex-col grow overflow-y-auto', {
                    'p-5 gap-2 rounded-b-xl': !maxSmall,
                  })
                "
              >
                <NotificationItem
                  v-for="notification in notifications"
                  :key="notification.id"
                  v-bind="notification"
                  @click.stop="handleNotificationClicked(notification)"
                />
              </div>
              <span
                v-if="hasMore && !isFetching"
                ref="loadMore"
                class="relative w-full h-24 md:h-20 bottom-24 md:bottom-20"
              />
            </div>
          </Tab>
        </Tabs>
        <NotificationSettings
          v-model:open="openSettings"
          :buildings="buildings"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, inject, onUnmounted, ref, watch } from "vue";
import { Dialog } from "@/SharedComponents/Dialog";
import { State } from "@/SharedComponents/State";
import { Tabs, Tab } from "@/SharedComponents/Tabs";
import { toast } from "@/SharedComponents/Toaster";
import { NotificationSettings } from "@/Pages/Notifications";
import {
  NotificationActions,
  NotificationItem,
  NotificationsHeader,
} from "@/Pages/Notifications/Partials";
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  QUERY_KEY,
} from "@/Composables/useNotifications";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useIntersectionObserver } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { cn } from "@/Utils/cn";

const { maxSmall } = useBreakpoints();
const { t } = useI18n();

const openSettings = ref(undefined);

const emit = defineEmits(["update:open"]);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  buildings: { type: Array, default: () => [] },
  unreadCount: { type: [String, Number], required: false },
});

const activeTabIndex = ref(0);

watch(
  () => props.open,
  (value) => {
    if (value) {
      activeTabIndex.value = 0;
    }
  }
);

const tabs = computed(() => [
  {
    id: "unread",
    label: t("components.notifications.tabs.unread"),
    badge: props.unreadCount,
  },
  { id: "all", label: t("components.notifications.tabs.all") },
]);

const activeTab = computed(() => tabs.value[activeTabIndex.value]);

const { isLoading, data, fetchNextPage, hasNextPage, isFetching } =
  useNotifications();

const notifications = computed(() => {
  if (isLoading.value) {
    return [];
  }
  let notifications = data.value.pages.flatMap((p) => p.data);
  if (activeTab.value.id === "unread") {
    return notifications.filter((n) => !n.read_at);
  }
  return notifications;
});

const hasMore = computed(() => {
  if (!hasNextPage.value) {
    return false;
  }
  if (activeTab.value.id === "unread") {
    return notifications.value.length < props.unreadCount;
  }
  return true;
});

const { mutate: markAsRead } = useMarkAsRead();
const { mutate: markAllAsRead } = useMarkAllAsRead();

function handleNotificationClicked(notification) {
  markAsRead(notification);
  emit("update:open", false);
  showTaskDetails(
    notification.data.task_id,
    ["comment.tagged", "task.comment"].includes(notification.type)
      ? "comments"
      : null
  );
}

function showTaskDetails(taskId, tab = null) {
  router.visit(route("tasks.show", { task: taskId, tab }), {
    preserveState: true,
    preserveScroll: true,
  });
}

const loadMore = ref(null);

const { stop } = useIntersectionObserver(loadMore, ([{ isIntersecting }]) => {
  if (isIntersecting && !isFetching.value) {
    fetchNextPage.value();
  }
});

onUnmounted(() => {
  stop();
});

const emitter = inject("emitter");

emitter.on("notification.problem-reported", () => {
  invalidateQueries();
  toast.info(t("toasts.notifications.problem_reported"));
});

emitter.on("notification.problem-upvoted", () => {
  invalidateQueries();
  toast.info(t("toasts.notifications.problem_upvoted"));
});

emitter.on("notification.task-comment", () => {
  invalidateQueries();
  toast.info(t("toasts.notifications.new_task_comment"));
});

emitter.on("notification.comment-tagged", () => {
  invalidateQueries();
  toast.info(t("toasts.notifications.tagged_in_comment"));
});

emitter.on("notification.task-assigned", () => {
  invalidateQueries();
  toast.info(t("toasts.notifications.task_assigned"));
});

const queryClient = useQueryClient();

function invalidateQueries() {
  queryClient.invalidateQueries({ queryKey: QUERY_KEY });
}
</script>
